import React, { useState } from "react";
import { CURRENCY } from "../../constants";
import {
  useCbeWithdrawMutation,
  useTelebirrWithdrawMutation,
  useArifMpesaWithdrawMutation,
} from "../../store/apis";
import { useSelector } from "react-redux";
import { selectUser, selectWalletBalance } from "../../store/auth-slice";
import { handleApiError } from "../../errors";
import { toastService } from "../../screens/notificationPage/ToastContainer";

const WithdrawComponent = () => {
  const user = useSelector(selectUser);
  const walletBalance = useSelector(selectWalletBalance);
  const [withdrawAmount, setWithdrawAmount] = useState(100);
  const [phoneNumber, setPhoneNumber] = useState(user?.username || "");
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [bankAccount, setBankAccount] = useState("");
  const [accountName, setAccountName] = useState("");

  // Payment method mutations
  const [telebirrWithdraw, { isLoading: isTelebirrLoading }] =
    useTelebirrWithdrawMutation();
  const [cbeWithdraw, { isLoading: isCbeLoading }] = useCbeWithdrawMutation();
  const [mpesaWithdraw, { isLoading: isMpesaLoading }] =
    useArifMpesaWithdrawMutation();

  // Quick amount options
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000];

  const handleWithdraw = async () => {
    if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
      toastService.error("Please enter a valid withdrawal amount");
      return;
    }

    if (withdrawAmount > walletBalance) {
      toastService.error("Insufficient balance for withdrawal");
      return;
    }

    if (!phoneNumber && expandedMethod !== "cbe") {
      toastService.error("Please enter a phone number");
      return;
    }

    if (expandedMethod === "cbe" && (!bankAccount || !accountName)) {
      toastService.error("Please enter both bank account and account name");
      return;
    }

    try {
      let response;
      const withdrawalData = {
        amount: withdrawAmount,
        phone: phoneNumber,
        lang: "EN",
      };

      // Add bank details for CBE method
      if (expandedMethod === "cbe") {
        withdrawalData.bankAccount = bankAccount;
        withdrawalData.accountName = accountName;
      }

      switch (expandedMethod) {
        case "telebirr":
          response = await telebirrWithdraw(withdrawalData).unwrap();
          break;
        case "cbe":
          response = await cbeWithdraw(withdrawalData).unwrap();
          break;
        case "mpesa":
          response = await mpesaWithdraw(withdrawalData).unwrap();
          break;
        default:
          toastService.error("Please select a payment method");
          return;
      }

      toastService.success(
        response?.msg || "Withdrawal initiated successfully"
      );
    } catch (error) {
      handleApiError(error, {
        showToast: true,
        toastPosition: "top-right",
      });
    }
  };

  const handleQuickAmount = (amount) => {
    setWithdrawAmount(amount);
  };

  const toggleExpand = (method) => {
    setExpandedMethod(expandedMethod === method ? null : method);
  };

  const isLoading = isTelebirrLoading || isCbeLoading || isMpesaLoading;

  return (
    <div className="p-4">
      {/* Available Balance */}
      <div className="mb-6 p-4 bg-[#0F2730] rounded-lg border border-gray-700">
        <div className="text-gray-400 mb-1 text-sm">Available Balance</div>
        <div className="text-white text-2xl font-bold">
          {CURRENCY} {walletBalance || "0.00"}
        </div>
      </div>

      {/* Payment Method Accordion */}
      <div className="space-y-3">
        {/* TeleBirr */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("telebirr")}
          >
            <div className="flex items-center">
              <img
                src="https://dash.bet/files/operators/additional_img/teleBirr.svg"
                alt="TeleBirr"
                className="h-8 mr-3"
              />
              <span>TELEBIRR</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "telebirr" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {expandedMethod === "telebirr" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                    max={walletBalance}
                  />
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
                    {CURRENCY}
                  </span>
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">
                  Quick Amounts
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleQuickAmount(amount)}
                      disabled={amount > walletBalance}
                      className={`py-2 rounded-lg text-sm ${
                        withdrawAmount === amount
                          ? "bg-nav text-white font-bold"
                          : amount > walletBalance
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-shadeGreen text-black hover:bg-[#163A47]"
                      }`}
                    >
                      {CURRENCY}
                      {amount}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={isLoading || withdrawAmount > walletBalance}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading || withdrawAmount > walletBalance
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? "PROCESSING..." : "WITHDRAW"}
              </button>
            </div>
          )}
        </div>

        {/* CBE */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("cbe")}
          >
            <div className="flex items-center">
              <img
                src="https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/d1/4c/78/d14c7876-5624-5e61-4ec6-2f7ca50338c3/AppIcon-0-0-1x_U007emarketing-0-0-0-7-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/512x512bb.jpg"
                alt="CBE"
                className="h-8 w-8 mr-3 rounded-md"
              />
              <span>CBE BIRR</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "cbe" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {expandedMethod === "cbe" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Account Name</label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500"
                  placeholder="Enter account name"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Bank Account</label>
                <input
                  type="text"
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500"
                  placeholder="Enter bank account number"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                    max={walletBalance}
                  />
                </div>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={isLoading || withdrawAmount > walletBalance}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading || withdrawAmount > walletBalance
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? "PROCESSING..." : "WITHDRAW"}
              </button>
            </div>
          )}
        </div>

        {/* Mpesa */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("mpesa")}
          >
            <div className="flex items-center">
              <img
                src="https://tse4.mm.bing.net/th/id/OIP.Ba3yksT-VX-5AeF8aQPJsQHaEK?rs=1&pid=ImgDetMain"
                alt="M-Pesa"
                className="h-8 mr-3"
              />
              <span>MPESA</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "mpesa" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {expandedMethod === "mpesa" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                    max={walletBalance}
                  />
                </div>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={isLoading || withdrawAmount > walletBalance}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading || withdrawAmount > walletBalance
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? "PROCESSING..." : "WITHDRAW"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawComponent;
